import type { IshikawaHypothesis, RootCauseAnalysis } from '../types/issue';
import { ensureIshikawa, ishikawaSummary } from './causeDraft';
import { isCauseConfirmed } from './problemCause';

export type CauseWorkFocus = 'ishikawa' | 'priority' | 'investigate' | 'prove' | 'consolidate' | 'plan';

export type CauseCheckAnswer = 'yes' | 'no' | 'unknown';

export interface CauseCheckView {
  causeEffect: CauseCheckAnswer;
  explainsPhenomenon: CauseCheckAnswer;
  technicallyBlockable: CauseCheckAnswer;
  unwantedEffects: CauseCheckAnswer;
  causeEffectNote: string;
  explainsPhenomenonNote: string;
  technicallyBlockableNote: string;
  unwantedEffectsNote: string;
}

export const EMPTY_CAUSE_CHECK: CauseCheckView = {
  causeEffect: 'unknown',
  explainsPhenomenon: 'unknown',
  technicallyBlockable: 'unknown',
  unwantedEffects: 'unknown',
  causeEffectNote: '',
  explainsPhenomenonNote: '',
  technicallyBlockableNote: '',
  unwantedEffectsNote: '',
};

function hypotheses(analysis: RootCauseAnalysis | null): IshikawaHypothesis[] {
  return ensureIshikawa(analysis?.ishikawa).flatMap((group) => (
    group.causes.filter((cause): cause is IshikawaHypothesis => typeof cause !== 'string')
  ));
}

/** Próximo trabalho da análise. A causa consolidada é a única que abre o plano. */
export function causeWorkCta(analysis: RootCauseAnalysis | null): { label: string; focus: CauseWorkFocus } {
  const summary = ishikawaSummary(analysis?.ishikawa);
  if (isCauseConfirmed({ root_cause_analysis: analysis })) {
    return { label: 'Definir plano de ação', focus: 'plan' };
  }
  if (summary.total === 0) {
    return analysis?.root_cause_description?.trim()
      ? { label: 'Consolidar causa', focus: 'consolidate' }
      : { label: 'Levantar causas potenciais', focus: 'ishikawa' };
  }
  if (summary.confirmed > 0) return { label: 'Consolidar causa', focus: 'consolidate' };
  const ready = hypotheses(analysis).some((item) => (
    item.status !== 'discarded'
    && Boolean(item.investigation?.conclusion?.trim())
    && Boolean(item.investigation?.evidence?.trim())
  ));
  if (ready) return { label: 'Comprovar causa', focus: 'prove' };
  const prioritized = hypotheses(analysis).some((item) => (
    item.status !== 'discarded' && (item.probable || item.status === 'investigating')
  ));
  if (!prioritized) return { label: 'Selecionar causas prováveis', focus: 'priority' };
  return { label: 'Investigar hipótese', focus: 'investigate' };
}

type Verdict = 'consistent' | 'uncertain' | 'inconsistent';

function verdict(answer: CauseCheckAnswer, unwanted: boolean): Verdict {
  if (answer === 'unknown') return 'uncertain';
  if (unwanted) return answer === 'no' ? 'consistent' : 'inconsistent';
  return answer === 'yes' ? 'consistent' : 'inconsistent';
}

function countLabel(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Resumo do teste. Quatro consistentes viram 4/4. Não confirma a causa. */
export function consistencySummary(check: Pick<CauseCheckView, 'causeEffect' | 'explainsPhenomenon' | 'technicallyBlockable' | 'unwantedEffects'>): string {
  const verdicts = [
    verdict(check.causeEffect, false),
    verdict(check.explainsPhenomenon, false),
    verdict(check.technicallyBlockable, false),
    verdict(check.unwantedEffects, true),
  ];
  const consistent = verdicts.filter((item) => item === 'consistent').length;
  if (consistent === 4) return '4/4 consistentes';
  const uncertain = verdicts.filter((item) => item === 'uncertain').length;
  const inconsistent = verdicts.filter((item) => item === 'inconsistent').length;
  return [
    consistent > 0 ? countLabel(consistent, 'consistente', 'consistentes') : '',
    uncertain > 0 ? countLabel(uncertain, 'incerto', 'incertos') : '',
    inconsistent > 0 ? countLabel(inconsistent, 'não consistente', 'não consistentes') : '',
  ].filter(Boolean).join(' · ');
}
